import { Provider } from 'store/api/enums';

export const getIs3rdPartyProvider = (
  providerName: string | null | undefined
): boolean => {
  const p = providerName?.toLowerCase();
  return (
    p !== Provider.EXCEL.toLowerCase() && p !== Provider.RECEIPTS.toLowerCase()
  );
};

/** Org shape used to decide whether provider-backed fetches should run. */
type OrganisationProviderFetchTarget = {
  providerName?: string | null;
  connectedProperly?: boolean;
};

/**
 * Whether COA, tracking categories, and transaction items should be fetched.
 * Excel/Receipts always qualify; connected providers (e.g. Xero, Zoho, QuickBooks)
 * require `connectedProperly`.
 */
export const shouldFetchOrganisationProviderData = (
  organisation: OrganisationProviderFetchTarget | null | undefined
): boolean => {
  if (!organisation) return false;
  const providerName = organisation.providerName?.toLowerCase();
  if (
    providerName === Provider.EXCEL.toLowerCase() ||
    providerName === Provider.RECEIPTS.toLowerCase()
  ) {
    return true;
  }
  return !!organisation.connectedProperly;
};

/**
 * When true, entity details should only allow the Tax Returns tab — used for
 * disconnected third-party providers (Xero, Zoho, QuickBooks, etc.).
 * Excel and Receipts are never restricted.
 */
export const shouldRestrictDisconnectedProviderTabs = (
  organisation: OrganisationProviderFetchTarget | null | undefined
): boolean => {
  if (!organisation?.providerName) return false;
  return !shouldFetchOrganisationProviderData(organisation);
};

/** QuickBooks and Zoho Books (contact filtering, etc.). */
export const isQuickBooksOrZohoProvider = (
  providerName: string | null | undefined
): boolean => {
  const p = String(providerName ?? '').toLowerCase();
  return (
    p === Provider.QUICKBOOKS ||
    p === Provider.ZOHO ||
    p.includes('quickbook')
  );
};

/** Xero, QuickBooks, and Zoho chart-of-accounts rows include an account code. */
export const showsCoaAccountCodeColumn = (
  providerName: string | null | undefined
): boolean => {
  const p = String(providerName ?? '').toLowerCase();
  return (
    p === Provider.XERO ||
    p === Provider.QUICKBOOKS ||
    p === Provider.ZOHO ||
    p.includes('quickbook')
  );
};

/**
 * Quantity + unit price columns on the entity transactions table.
 * Xero / QuickBooks / Zoho / Receipts (not Excel).
 * Total / Net are derived from qty, unitPrice, discount, tax, and taxInclusive.
 */
export const showsQuantityAndUnitPriceColumns = (
  providerName: string | null | undefined
): boolean => {
  const p = String(providerName ?? '').toLowerCase();
  return (
    showsCoaAccountCodeColumn(providerName) ||
    p === Provider.RECEIPTS
  );
};